import { homeCurrencyLabel } from '@/lib/org-locale';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  AddCurrencyDialog,
  ImportRatesDialog,
  RateDialog,
} from '@/components/currencies/currency-dialogs';
import { RevalueDialog } from '@/components/currencies/revalue-dialog';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { TextLink } from '@/components/menu-link';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError, api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import {
  type CurrenciesOverview,
  type ExchangeRate,
  rateLine,
  type Revaluation,
  SOURCE_LABEL,
  useCurrencies,
  useExchangeRates,
  useHomeCurrency,
  useRevaluations,
} from '@/lib/currencies';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate, formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

type Tab = 'currencies' | 'rates' | 'revaluations';

export const Route = createFileRoute('/settings/currencies')({
  component: CurrenciesPage,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    s['tab'] === 'rates' || s['tab'] === 'revaluations' ? { tab: s['tab'] } : {},
});

const TABS: Array<[Tab, string]> = [
  ['currencies', 'Currencies'],
  ['rates', 'Exchange rates'],
  ['revaluations', 'Revaluations'],
];

/**
 * Currencies (document 06 G10; QBO gear › Lists › Currencies): the company's currencies with
 * their latest rates, the rate history (typed, imported or fetched from the rate provider) and
 * Revalue currency with its history (A13).
 */
function CurrenciesPage() {
  const orgId = useOrgId();
  const { tab = 'currencies' } = Route.useSearch();
  const me = useMe();
  const staff = me.data?.isStaff ?? false;
  const overview = useCurrencies();
  const homeFallback = useHomeCurrency();
  const [adding, setAdding] = useState(false);
  const [rateFor, setRateFor] = useState<{ currency: string; date?: string; rate?: string } | null>(
    null,
  );
  const [newRate, setNewRate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [revalue, setRevalue] = useState<string | null | false>(false);
  const qc = useQueryClient();
  const getRates = useMutation({
    mutationFn: () =>
      api<ExchangeRate[]>(`/orgs/${orgId}/exchange-rates/fetch`, {
        method: 'POST',
        body: JSON.stringify({ date: todayIso() }),
      }),
    onSuccess: async (rows) => {
      await qc.invalidateQueries({ queryKey: ['currencies', orgId] });
      toast(`Today's rates: ${rows.map((r) => `${r.currency} ${r.rate}`).join(', ')}.`);
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
  });
  const remove = useMutation({
    mutationFn: (code: string) =>
      api<CurrenciesOverview>(`/orgs/${orgId}/currencies/${code}`, { method: 'DELETE' }),
    onSuccess: async (_r, code) => {
      await qc.invalidateQueries({ queryKey: ['currencies', orgId] });
      toast(`${code} removed.`);
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
  });

  if (!orgId)
    return <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />;
  const o = overview.data;
  const codes = o?.currencies.map((c) => c.code) ?? [];
  const home = o?.homeCurrency ?? homeFallback;

  return (
    <>
      <PageHeader
        title="Currencies"
        description={`Home currency ${home}. Rates are how many ${home} one unit of the currency buys.`}
        actions={
          staff && o?.enabled ? (
            <>
              {o.rateProvider ? (
                <Button
                  variant="outline"
                  disabled={!codes.length || getRates.isPending}
                  onClick={() => getRates.mutate()}
                >
                  {getRates.isPending ? 'Getting rates…' : "Get today's rates"}
                </Button>
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-1" disabled={!codes.length}>
                    More <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => setNewRate(true)}>
                    Enter an exchange rate
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setImporting(true)}>
                    Import exchange rates
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setRevalue(null)}>
                    Revalue currency
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button disabled={!o.available.length} onClick={() => setAdding(true)}>
                Add currency
              </Button>
            </>
          ) : null
        }
      />
      {overview.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : overview.isError ? (
        <EmptyState title="Could not load currencies" hint={overview.error.message} />
      ) : !o!.enabled ? (
        <EmptyState
          title="Multicurrency is off"
          hint="Switch it on in Company settings › Advanced › Currency to bill customers, pay vendors and keep bank accounts in other currencies."
          action={
            <Button asChild variant="outline">
              <Link to="/settings/company" search={{ tab: 'advanced' }}>
                Company settings
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="mb-3 flex gap-6 border-b" role="tablist">
            {TABS.map(([key, text]) => (
              <Link
                key={key}
                role="tab"
                aria-selected={tab === key}
                to="/settings/currencies"
                search={key === 'currencies' ? {} : { tab: key }}
                className={cn(
                  '-mb-px border-b-2 px-1 pb-2 text-sm',
                  tab === key
                    ? 'border-primary font-medium text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {text}
              </Link>
            ))}
          </div>
          {tab === 'currencies' ? (
            <CurrencyList
              o={o!}
              staff={staff}
              onRate={(currency) => setRateFor({ currency })}
              onRevalue={(c) => setRevalue(c)}
              onRemove={(c) => {
                if (window.confirm(`Remove ${c} and its exchange rates?`)) remove.mutate(c);
              }}
            />
          ) : tab === 'rates' ? (
            <RateHistory
              codes={codes}
              home={home}
              staff={staff}
              onEdit={(r) => setRateFor({ currency: r.currency, date: r.date, rate: r.rate })}
            />
          ) : (
            <RevaluationHistory home={home} staff={staff} />
          )}
        </>
      )}
      {o ? (
        <>
          <AddCurrencyDialog
            open={adding}
            onClose={() => setAdding(false)}
            available={o.available}
          />
          <RateDialog
            open={Boolean(rateFor) || newRate}
            onClose={() => {
              setRateFor(null);
              setNewRate(false);
            }}
            currencies={codes}
            home={home}
            initial={rateFor}
          />
          <ImportRatesDialog open={importing} onClose={() => setImporting(false)} />
          <RevalueDialog
            open={revalue !== false}
            onClose={() => setRevalue(false)}
            currencies={codes}
            home={home}
            initialCurrency={revalue || null}
          />
        </>
      ) : null}
    </>
  );
}

function CurrencyList({
  o,
  staff,
  onRate,
  onRevalue,
  onRemove,
}: {
  o: CurrenciesOverview;
  staff: boolean;
  onRate: (currency: string) => void;
  onRevalue: (currency: string) => void;
  onRemove: (currency: string) => void;
}) {
  if (!o.currencies.length)
    return (
      <EmptyState
        title="No other currencies yet"
        hint="Add the currencies your customers, vendors and bank accounts use."
      />
    );
  return (
    <div className="rounded-lg border bg-card">
      <Table aria-label="Currencies">
        <TableHeader>
          <TableRow>
            <TableHead>Currency</TableHead>
            <TableHead className="text-right">Exchange rate</TableHead>
            <TableHead className="w-[140px]">As of</TableHead>
            <TableHead className="w-[160px] text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell>
              {homeCurrencyLabel(o.homeCurrency)}{' '}
              <span className="text-xs text-muted-foreground">(home currency)</span>
            </TableCell>
            <TableCell className="tnum text-right">1</TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
          {o.currencies.map((c) => (
            <TableRow key={c.code} data-testid="currency-row">
              <TableCell>
                {c.code} – {c.name}
              </TableCell>
              <TableCell className="tnum text-right">
                {c.latestRate ? rateLine(c.code, c.latestRate, o.homeCurrency) : 'No rate yet'}
              </TableCell>
              <TableCell>{c.latestRateDate ? formatDate(c.latestRateDate) : ''}</TableCell>
              <TableCell className="text-right">
                {staff ? (
                  <div className="inline-flex">
                    <Button variant="link" className="h-auto px-1" onClick={() => onRate(c.code)}>
                      Edit exchange rate
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={`More for ${c.code}`}>
                          <ChevronDown className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => onRevalue(c.code)}>
                          Revalue
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/settings/currencies" search={{ tab: 'rates' }}>
                            Rate history
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={c.inUse}
                          title={
                            c.inUse
                              ? 'Used by customers, vendors, accounts or transactions'
                              : undefined
                          }
                          onSelect={() => onRemove(c.code)}
                        >
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RateHistory({
  codes,
  home,
  staff,
  onEdit,
}: {
  codes: string[];
  home: string;
  staff: boolean;
  onEdit: (r: ExchangeRate) => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [currency, setCurrency] = useState('');
  const rates = useExchangeRates(currency || undefined);
  const del = useMutation({
    mutationFn: (id: string) =>
      api<void>(`/orgs/${orgId}/exchange-rates/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['currencies', orgId] });
      toast('Exchange rate deleted. Transactions keep the rate they were saved with.');
    },
  });
  return (
    <>
      <div className="mb-3 flex items-center gap-3">
        <NativeSelect
          aria-label="Currency"
          className="w-[200px] bg-card"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        >
          <option value="">All currencies</option>
          {codes.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </NativeSelect>
        <p className="text-sm text-muted-foreground">
          A transaction uses the latest rate on or before its date unless another rate is entered on
          it.
        </p>
      </div>
      {rates.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !rates.data?.length ? (
        <EmptyState
          title="No exchange rates yet"
          hint="Enter rates, import a CSV or get today's rates from the rate provider."
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table aria-label="Exchange rates">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[130px]">Date</TableHead>
                <TableHead className="w-[100px]">Currency</TableHead>
                <TableHead className="text-right">Rate ({home})</TableHead>
                <TableHead className="w-[150px]">Source</TableHead>
                <TableHead className="w-[170px]">Changed</TableHead>
                <TableHead className="w-[150px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rates.data.map((r) => (
                <TableRow key={r.id} data-testid="rate-row">
                  <TableCell>{formatDate(r.date)}</TableCell>
                  <TableCell>{r.currency}</TableCell>
                  <TableCell className="tnum text-right">{r.rate}</TableCell>
                  <TableCell>
                    {SOURCE_LABEL[r.source]}
                    {r.provider ? ` (${r.provider})` : ''}
                  </TableCell>
                  <TableCell>{formatDateTime(r.updatedAt)}</TableCell>
                  <TableCell className="text-right">
                    {staff ? (
                      <>
                        <Button variant="link" className="h-auto px-1" onClick={() => onEdit(r)}>
                          Edit
                        </Button>
                        <Button
                          variant="link"
                          className="h-auto px-1"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete the ${r.currency} rate of ${formatDate(r.date)}?`,
                              )
                            )
                              del.mutate(r.id);
                          }}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

function RevaluationHistory({ home, staff }: { home: string; staff: boolean }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const list = useRevaluations();
  const [undoing, setUndoing] = useState<Revaluation | null>(null);
  const undo = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<Revaluation>(`/orgs/${orgId}/revaluations/${id}/undo`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      toast(`${r.currency} revaluation of ${formatDate(r.date)} undone.`);
      setUndoing(null);
    },
  });
  if (list.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!list.data?.length)
    return (
      <EmptyState
        title="No revaluations yet"
        hint="Revalue a currency at month end to show open foreign balances at that day's rate."
      />
    );
  return (
    <>
      <div className="rounded-lg border bg-card">
        <Table aria-label="Revaluations">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Date</TableHead>
              <TableHead className="w-[100px]">Currency</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Unrealised gain or loss ({home})</TableHead>
              <TableHead>Entries</TableHead>
              <TableHead className="w-[110px]">Status</TableHead>
              <TableHead className="w-[100px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data.map((r) => (
              <TableRow key={r.id} data-testid="revaluation-row">
                <TableCell>{formatDate(r.date)}</TableCell>
                <TableCell>{r.currency}</TableCell>
                <TableCell className="tnum text-right">{r.rate}</TableCell>
                <TableCell className="tnum text-right">{formatAmount(r.gain)}</TableCell>
                <TableCell className="text-sm">
                  <TextLink to={`/accounting/journal-entries/${r.entryId}`}>
                    {formatDate(r.date)}
                  </TextLink>{' '}
                  · reversed{' '}
                  <TextLink to={`/accounting/journal-entries/${r.reversalEntryId}`}>
                    {formatDate(r.reversalDate)}
                  </TextLink>
                </TableCell>
                <TableCell>
                  {r.status === 'void' ? (
                    <span title={r.voidReason ?? undefined}>Undone</span>
                  ) : (
                    'Posted'
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {staff && r.status === 'posted' ? (
                    <Button variant="link" className="h-auto px-1" onClick={() => setUndoing(r)}>
                      Undo
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <UndoDialog
        revaluation={undoing}
        pending={undo.isPending}
        error={undo.error ? undo.error.message : null}
        onCancel={() => setUndoing(null)}
        onConfirm={(reason) => undo.mutate({ id: undoing!.id, reason })}
      />
    </>
  );
}

/** Undo a revaluation: both entries reverse on their own dates; the reason is audited. */
function UndoDialog({
  revaluation,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  revaluation: Revaluation | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => setReason(''), [revaluation]);
  return (
    <Dialog open={Boolean(revaluation)} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Undo the {revaluation?.currency} revaluation of{' '}
            {revaluation ? formatDate(revaluation.date) : ''}
          </DialogTitle>
          <DialogDescription>
            The revaluation entry and its next-day reversal are both reversed on their own dates.
            The revaluation stays in this list as undone.
          </DialogDescription>
        </DialogHeader>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <Field label="Reason" hint="Recorded in the audit log.">
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={pending || !reason.trim()}
            onClick={() => onConfirm(reason.trim())}
          >
            {pending ? 'Undoing…' : 'Undo revaluation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
